import React, { useCallback, useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "components/common/TableContainer";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import {
  getEmployees,
  updateEmployeeStatus,
} from "features/Employees/services/EmployeeService";
import { deleteUser } from "features/Users/services/UserService";
import {
  Employee,
  departmentDisplayName,
  designationDisplayName,
  employmentCategoryDisplayName,
} from "features/Employees/types/employee.types";
import { getDocumentTitle } from "utils/helpers";
import Avatar from "components/common/Avatar";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { EmployeeOverviewTabParam } from "features/Employees/EmployeeOverview/overviewTabSearchParams";

const API_URL = import.meta.env.VITE_API_URL || "";

function employeeDisplayName(emp: Employee): string {
  const fromUser = [emp.user?.firstName, emp.user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fromUser) return fromUser;
  const fromEmp = [emp.firstName, emp.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fromEmp) return fromEmp;
  return emp.user?.email?.trim() || emp.email?.trim() || "—";
}

function employeeAvatarUrl(emp: Employee): string | undefined {
  const raw = emp.user?.avatar;
  if (!raw) return undefined;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `${API_URL}/uploads/${raw}`;
}

const EmployeeList = () => {
  document.title = getDocumentTitle("Employees");
  const { can } = useAccess();
  const canReadEmp = can(Permissions.EMPLOYEE.READ);
  const canUpdateEmp = can(Permissions.EMPLOYEE.UPDATE);
  const canReadSalaryStructure = can(Permissions.EMPLOYEE_SALARY_STRUCTURE.READ);
  const canDeleteUser = can(Permissions.USER.DELETE);

  const tableRef = useRef<() => void>(null);
  const [selectedRecord, setSelectedRecord] = useState<Employee | null>(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const openDeactivate = useCallback((emp: Employee) => {
    setSelectedRecord(emp);
    setDeactivateOpen(true);
  }, []);

  const openActivate = useCallback((emp: Employee) => {
    setSelectedRecord(emp);
    setActivateOpen(true);
  }, []);

  const openDelete = useCallback((emp: Employee) => {
    setSelectedRecord(emp);
    setDeleteModal(true);
  }, []);

  const handleDelete = async () => {
    if (!selectedRecord) return;
    try {
      const res = await deleteUser(selectedRecord.userId);
      if (res.success) {
        toast.success(res.message ?? "User deleted.");
        setDeleteModal(false);
        setSelectedRecord(null);
        tableRef.current?.();
      } else {
        toast.error(res.message ?? "Delete failed.");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Delete failed.");
    }
  };

  const handleStatusChange = async (isPayrollable: boolean) => {
    if (!selectedRecord) return;
    try {
      const res = await updateEmployeeStatus(selectedRecord.id, isPayrollable);
      if (res.success) {
        toast.success(res.message ?? (isPayrollable ? "Employee included in payroll" : "Employee excluded from payroll"));
        tableRef.current?.();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to update status");
    } finally {
      setDeactivateOpen(false);
      setActivateOpen(false);
      setSelectedRecord(null);
    }
  };

  const columns: ColumnDef<Employee>[] = useMemo(
    () => [
      IndexColumn<Employee>(),

      {
        header: "Employee",
        accessorKey: "firstName",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const emp = row.original;
          const name = employeeDisplayName(emp);
          const email = (emp.user?.email ?? emp.email ?? "").trim();
          const code = emp.code?.trim();
          const avatarSrc = employeeAvatarUrl(emp);

          return (
            <div className="d-flex align-items-center gap-2 min-w-0">
              <div className="avatar-sm flex-shrink-0">
                <Avatar
                  name={name === "—" ? email || "Employee" : name}
                  image={avatarSrc}
                />
              </div>
              <div className="flex-grow-1 min-w-0">
                {canReadEmp ? (
                  <Link
                    to={`/employees/${emp.id}`}
                    className="fw-semibold text-body text-decoration-none d-block text-truncate"
                    title={name !== "—" ? name : undefined}
                  >
                    {name}
                  </Link>
                ) : (
                  <span className="fw-semibold text-body d-block text-truncate">
                    {name}
                  </span>
                )}
                {code ? (
                  <span className="text-muted small d-block text-truncate">
                    {code}
                  </span>
                ) : null}
                {email ? (
                  <a
                    href={`mailto:${email}`}
                    className="text-muted small text-truncate d-block text-decoration-none"
                    title={email}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-mail-line align-middle me-1 fs-12" />
                    {email}
                  </a>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        header: "Payroll",
        id: "isPayrollable",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const payrollable = row.original.isPayrollable !== false;
          return payrollable ? (
            <span className="badge bg-success-subtle text-success">Included</span>
          ) : (
            <span className="badge bg-danger-subtle text-danger">Excluded</span>
          );
        },
      },
      {
        header: "Join Date",
        accessorKey: "joinDate",
        enableColumnFilter: false,
        cell: ({ row }) => <DateOnly date={row.original.joinDate} />,
      },
      {
        header: "Department",
        id: "departmentName",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="text-body">
            {departmentDisplayName(row.original)}
          </span>
        ),
      },
      {
        header: "Designation",
        id: "designationName",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="text-body">
            {designationDisplayName(row.original)}
          </span>
        ),
      },
      {
        header: "Employment category",
        id: "employmentCategoryName",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="text-body">
            {employmentCategoryDisplayName(row.original)}
          </span>
        ),
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const d = row.original.createdAt;
          return d ? (
            <DateTime date={d} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const emp = row.original;
          const isPayrollable = emp.isPayrollable !== false;
          const hasAny = canReadEmp || canUpdateEmp || canReadSalaryStructure || canDeleteUser;
          if (!hasAny) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Employee actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadEmp ? (
                  <DropdownItem tag={Link} to={`/employees/${emp.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {canReadSalaryStructure ? (
                  <DropdownItem
                    tag={Link}
                    to={`/employees/${emp.id}?tab=${EmployeeOverviewTabParam.SALARY_STRUCTURES}`}
                  >
                    <i className="ri-wallet-3-line align-bottom me-2 text-muted" />
                    Salary structures
                  </DropdownItem>
                ) : null}
                {canUpdateEmp ? (
                  <DropdownItem tag={Link} to={`/employees/edit/${emp.id}`}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canUpdateEmp ? (
                  <>
                    {canReadEmp || canReadSalaryStructure ? (
                      <DropdownItem divider />
                    ) : null}
                    {isPayrollable ? (
                      <DropdownItem
                        className="text-danger"
                        onClick={() => openDeactivate(emp)}
                      >
                        <i className="ri-forbid-line align-bottom me-2" />
                        Exclude from payroll
                      </DropdownItem>
                    ) : (
                      <DropdownItem
                        className="text-success"
                        onClick={() => openActivate(emp)}
                      >
                        <i className="ri-checkbox-circle-line align-bottom me-2" />
                        Include in payroll
                      </DropdownItem>
                    )}
                  </>
                ) : null}
                {canDeleteUser ? (
                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      className="text-danger"
                      onClick={() => openDelete(emp)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [openDeactivate, openActivate, openDelete, canReadEmp, canUpdateEmp, canReadSalaryStructure, canDeleteUser],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Employees" pageTitle="HR Management" />
        <Card>
          <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="card-title mb-0">Employees</h5>
            {can(Permissions.EMPLOYEE.CREATE) ? (
              <Link to="/employees/create" className="btn btn-primary">
                <i className="ri-add-line align-bottom me-1" />
                Add Employee
              </Link>
            ) : null}
          </CardHeader>
          <CardBody>
            <DeleteModal
              show={deleteModal}
              onDeleteClick={handleDelete}
              onCloseClick={() => { setDeleteModal(false); setSelectedRecord(null); }}
              description="Deleting this user will also permanently remove their linked employee record, salary structures, bank accounts, and all associated data."
            />
            <ConfirmActionModal
              show={deactivateOpen}
              title="Exclude from payroll"
              description={`${employeeDisplayName(selectedRecord ?? ({} as Employee))} will be excluded from future payroll runs. Their login access is unaffected. This can be reversed at any time.`}
              confirmText="Exclude"
              confirmColor="danger"
              onConfirm={() => handleStatusChange(false)}
              onClose={() => {
                setDeactivateOpen(false);
                setSelectedRecord(null);
              }}
            />
            <ConfirmActionModal
              show={activateOpen}
              title="Include in payroll"
              description={`${employeeDisplayName(selectedRecord ?? ({} as Employee))} will be included in future payroll runs again.`}
              confirmText="Include"
              confirmColor="success"
              onConfirm={() => handleStatusChange(true)}
              onClose={() => {
                setActivateOpen(false);
                setSelectedRecord(null);
              }}
            />
            <TableContainer
              columns={columns}
              fetchApi={getEmployees}
              pageSize={10}
              globalSearch={true}
              refreshRef={tableRef}
              SearchPlaceholder="Search by name or email..."
              divClass="table-responsive table-card mb-3"
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light text-muted"
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default EmployeeList;
